import { createRouter, createWebHashHistory, RouteLocation, RouteRecordRaw } from 'vue-router'

import { SettingsNames } from '@/widgets/sidebar'

import { useAsyncSettingsComponents } from '@/shared/composables/router'
import { pathNames } from '@/shared/utils/constants'

import { alarmItemSettingsBeforeEnter } from './alarmItemSettingsBeforeEnter'
import { alarmItemSettingsPropsFn } from './alarmItemSettingsPropsFn'
import { alarmSettingsBeforeEnter } from './alarmSettingsBeforeEnter'
import { beforeEnterPrepareToCall } from './before-enter'
import { beforeEachHook } from './beforeEachHook'
import { blackWhiteListBeforeEnter } from './blackWhiteListBeforeEnter'
import { callGuestSessionBeforeEnter } from './callGuestSessionBeforeEnter'
import { callQueuesBeforeEnter } from './callQueuesBeforeEnter'
import { callSessionBeforeEnter } from './callSessionBeforeEnter'
import { callSessionPropsFn } from './callSessionPropsFn'
import { chatBeforeEnter } from './chatBeforeEnter'
import { chatPropsFn } from './chatPropsFn'
import { chatSettingsBeforeEnter } from './chatSettingsBeforeEnter'
import { closeAside } from './closeAside'
import { equipmentBeforeEnter } from './equipmentBeforeEnter'
import { followMeEditScriptPropsFn } from './follow-me-edit-script-props-fn'
import { functionalKeysBeforeEnter } from './functionalKeysBeforeEnter'
import { functionalKeysPropsFn } from './functionalKeysPropsFn'
import { getEnabledSubLink } from './getEnabledSubLink'
import { guestViewChatPropsFn } from './guestViewChatPropsFn'
import { prepareToCallPropsFn } from './props-fn'
import { clearRouteChunkError, isRouteChunkLoadError, setRouteChunkError } from './route-chunk-error'
import { sessionsBeforeEnter } from './sessionsBeforeEnter'
import { terminalsListBeforeEnter } from './terminalsListBeforeEnter'
import { useApplicationLayout } from './useApplicationLayout'
import { useDefaultLayout } from './useDefaultLayout'
import { useGuestLayout } from './useGuestLayout'
import { useSupportLayout } from './useSupportLayout'
import { webClientBeforeEnter } from './webClientBeforeEnter'

const {
  IncomingCallHandling,
  BlackWhiteList,
  FollowMe,
  CreateScript,
  EditScript,
  ScriptList,
  SpeedDial,
  IvrPage,
  VoiceMailForwarding,
  SubscriberPickUp,
  QueueServices,
  NotificationSettings,
  NotificationsWrapper,
  TerminalsList,
  FunctionalKeys,
  TerminalsWrapper,
  GeneralSettings,
  SubscriberAliases,
  ServicesHandle,
  HandleServicesPackages,
  PinPasswordInput,
  CLIR,
  ConfidentialityWrapper,
  ViewInviteLogin,
  ViewConferenceInternalLinkDenied,
  GuestCallSession,
  ViewCallSession,
  ViewChat,
  SoundAndVideo,
  Calls,
  AccountSettings,
  ViewSettings,
  AboutApp,
  AlarmClock,
  ViewProfile,
  VoiceMail,
  AudioFilesSettings,
  AudioFilesTable,
  ASideMenuSettings,
  DesktopApplicationSettings,
  SchedulePage,
  PrepareToCall,
  ViewLogin,
  ViewMain,
  AlarmItem,
  AlarmPage,
  ViewTokenLogin,
  ByePage,
  Conference,
  EquipmentList,
  SessionsList,
  InterfaceSettings,
  LanguageSettings,
  ThemeSettings,
  ChatSettings,
  ContactGroupsSettings,
  AudioFilesCategory,
  AudioFilesCreate,
  MainMenu,
} = useAsyncSettingsComponents()

const redirectToLastMatched = (to: RouteLocation) => {
  const lastMatched = to.matched[to.matched.length - 1]
  const lastMatchedChildren = lastMatched?.children
  if (lastMatchedChildren?.length) {
    return lastMatchedChildren[0]!
  }
  return to
}

const routes: Array<RouteRecordRaw> = [
  useApplicationLayout({
    path: '/',
    component: ViewMain,
    children: [
      {
        name: pathNames.MAIN,
        path: '',
        meta: { title: 'common.main' },
        component: MainMenu,
      },
      {
        path: 'chat',
        meta: { title: 'common.chat' },
        redirect: '/',
      },
      {
        name: pathNames.CHAT,
        path: 'chat/:id',
        component: ViewChat,
        props: chatPropsFn,
        meta: { title: 'common.chat' },
        beforeEnter: chatBeforeEnter,
      },
      {
        name: pathNames.CALL_SESSION,
        path: '/session/:sessionId',
        component: ViewCallSession,
        props: callSessionPropsFn,
        meta: { title: 'common.session' },
        beforeEnter: callSessionBeforeEnter,
      },
      {
        name: pathNames.PREPARE_TO_CALL,
        path: '/prepare-to-call/:roomId',
        meta: { title: 'common.session' },
        component: PrepareToCall,
        beforeEnter: beforeEnterPrepareToCall,
        props: prepareToCallPropsFn,
      },
      {
        name: pathNames.CALENDAR,
        path: '/calendar',
        meta: { title: 'common.calendarMenuName' },
        component: SchedulePage,
        beforeEnter: webClientBeforeEnter,
      },
      {
        name: pathNames.MAIN_MENU,
        path: '/calls-menu',
        component: MainMenu,
      },
      {
        name: pathNames.PROFILE,
        path: '/profile',
        component: ViewProfile,
        meta: { title: 'common.voiceMailForwarding' },
        children: [
          {
            name: pathNames.PROFILE_VOICE_MAIL,
            path: 'voice-mail',
            component: VoiceMail,
          },
        ],
      },
      {
        path: '/settings',
        name: pathNames.VIEW_SETTINGS,
        component: ViewSettings,
        meta: { title: 'common.settings' },
        children: [
          {
            name: SettingsNames.general,
            path: 'general-settings',
            component: GeneralSettings,
            redirect: to => {
              const name = getEnabledSubLink(to.name)
              return { name }
            },
            children: [
              {
                name: 'subscriberAliases',
                path: 'subscriber-aliases',
                meta: { title: 'general.subscriberAliases' },
                component: SubscriberAliases,
              },
              {
                name: 'servicesHandle',
                path: 'services-handle',
                meta: { title: 'mainAside.servicesHandle' },
                component: ServicesHandle,
              },
              {
                name: 'handleServicesPackages',
                path: 'handle-services-packages',
                meta: { title: 'general.handleServicesPackages' },
                component: HandleServicesPackages,
              },
              {
                name: 'aSideMenuSettings',
                path: 'aside-menu-settings',
                meta: { title: 'general.SettingsMenu' },
                component: ASideMenuSettings,
              },
              {
                name: 'desktopApplicationSettings',
                path: 'desktop-settings',
                component: DesktopApplicationSettings,
              },
              {
                name: 'contactGroupsSettings',
                path: 'contact-groups',
                meta: { title: 'mainAside.handleContactGroups' },
                component: ContactGroupsSettings,
              },
            ],
          },
          {
            name: SettingsNames.soundAndVideo,
            path: 'sound-and-video',
            meta: { title: 'soundAndVideo.soundAndVideoSettings' },
            component: SoundAndVideo,
          },
          {
            name: SettingsNames.calls,
            path: 'calls',
            component: Calls,
            redirect: to => {
              const name = getEnabledSubLink(to.name)
              return { name }
            },
            children: [
              {
                name: 'incomingCallHandling',
                path: 'incoming-call-handling',
                meta: { title: 'callsSettings.incomingCallHandling' },
                component: IncomingCallHandling,
                // TODO(WUI-4375): добавить beforeEnter по FeaturesNames.FORWARD (как blackWhiteListBeforeEnter), deep-link сейчас открыт при скрытом UI
              },
              {
                name: 'blackWhiteList',
                path: 'black-white-list',
                meta: { title: 'callsSettings.blackWhiteListSettings' },
                component: BlackWhiteList,
                beforeEnter: blackWhiteListBeforeEnter,
              },
              {
                name: 'followMe',
                path: 'follow-me',
                meta: { title: 'callsSettings.followMe' },
                component: FollowMe,
                redirect: () => {
                  return { name: 'script-list', path: 'script-list' }
                },
                children: [
                  {
                    name: 'script-list',
                    path: 'script-list',
                    meta: { title: 'callsSettings.followMe' },
                    component: ScriptList,
                  },
                  {
                    name: 'edit-script',
                    path: 'edit-script/:id',
                    meta: { title: 'edit-script' },
                    component: EditScript,
                    props: followMeEditScriptPropsFn,
                  },
                  {
                    name: 'create-script',
                    path: 'create-script',
                    meta: { title: 'create-script' },
                    component: CreateScript,
                  },
                ],
              },
              {
                name: 'speedDial',
                path: 'speed-dial',
                meta: { title: 'common.speedDial' },
                component: SpeedDial,
              },
              {
                name: 'IVR',
                path: 'ivr',
                meta: { title: 'callsSettings.IVR' },
                component: IvrPage,
              },
              {
                name: 'voiceMailForwarding',
                path: 'voice-mail-forwarding',
                meta: { title: 'callsSettings.voiceMailSettings' },
                component: VoiceMailForwarding,
              },
              {
                name: 'subscriberPickUp',
                path: 'subscriber-pick-up',
                meta: { title: 'callsSettings.subscriberPickUp' },
                component: SubscriberPickUp,
              },
              {
                name: 'callQueues',
                path: 'queue-services',
                meta: { title: 'callsSettings.callQueues' },
                beforeEnter: callQueuesBeforeEnter,
                component: QueueServices,
              },
            ],
          },
          {
            name: SettingsNames.terminals,
            path: 'terminals',
            component: TerminalsWrapper,
            redirect: to => {
              const name = getEnabledSubLink(to.name)
              return { name }
            },
            children: [
              {
                name: 'terminalsList',
                path: 'terminals-list',
                meta: { title: 'terminals.terminalsList' },
                beforeEnter: terminalsListBeforeEnter,
                component: TerminalsList,
              },
              {
                name: 'functionalKeysSetup',
                path: 'functional-keys-setup/:mac',
                meta: { title: 'terminals.functionalKeysSettings' },
                component: FunctionalKeys,
                beforeEnter: functionalKeysBeforeEnter,
                props: functionalKeysPropsFn,
              },
              {
                name: 'equipment',
                path: 'equipment',
                meta: { title: 'common.devices' },
                beforeEnter: equipmentBeforeEnter,
                component: EquipmentList,
              },
              {
                name: 'authorizedSessions',
                path: 'sessions',
                meta: { title: 'terminals.sessions' },
                beforeEnter: sessionsBeforeEnter,
                component: SessionsList,
              },
            ],
          },
          {
            name: SettingsNames.account,
            path: 'account',
            meta: { title: 'account.accountSettings' },
            component: AccountSettings,
          },
          {
            name: SettingsNames.confidentiality,
            path: 'confidentiality',
            component: ConfidentialityWrapper,
            redirect: (to) => {
              const name = getEnabledSubLink(to.name)
              return { name }
            },
            children: [
              {
                name: 'pinPasswordInput',
                path: 'pin-password-page',
                meta: { title: 'confidentiality.pinPasswordInput' },
                component: PinPasswordInput,
              },
              {
                name: 'clir',
                path: 'c-l-i-r',
                meta: { title: 'callsSettings.antiCallerIdSettings' },
                component: CLIR,
              },
            ],
          },
          {
            name: SettingsNames.notifications,
            path: 'notifications',
            component: NotificationsWrapper,
            redirect: to => {
              const name = getEnabledSubLink(to.name)
              return { name }
            },
            children: [
              {
                name: 'notificationSettings',
                path: 'notifications-settings',
                meta: { title: 'mainAside.notificationSettings' },
                component: NotificationSettings,
              },
              {
                name: pathNames.ALARM_SCHEDULE,
                path: 'alarm-schedule',
                component: AlarmClock,
                redirect: () => {
                  return { name: 'alarm', path: 'alarm' }
                },
                children: [
                  {
                    name: 'alarm',
                    path: 'alarm',
                    meta: { title: 'common.alarm' },
                    component: AlarmPage,
                    beforeEnter: alarmSettingsBeforeEnter,
                  },
                  {
                    name: 'alarm-item',
                    path: 'alarm-item/:id?',
                    meta: { title: 'common.alarm' },
                    component: AlarmItem,
                    props: alarmItemSettingsPropsFn,
                    beforeEnter: alarmItemSettingsBeforeEnter,
                  },
                ],
              },
              {
                name: pathNames.AUDIO_FILES_SETTINGS,
                path: 'audio-file-settings',
                component: AudioFilesSettings,
                redirect: () => {
                  return { name: 'audiofiles-list', path: 'audiofiles-list' }
                },
                children:
                  [
                    {
                      name: 'audiofiles-list',
                      path: 'audiofiles-list',
                      meta: { title: 'common.audioFiles' },
                      component: AudioFilesTable,
                    },
                    {
                      name: 'audiofiles-categories',
                      path: 'audiofiles-categories',
                      meta: { title: 'notifications.createAudioFiles' },
                      component: AudioFilesCategory,
                    },
                    {
                      name: 'audiofiles-create',
                      path: 'audiofiles-create',
                      meta: { title: 'notifications.newCategoryTabName' },
                      component: AudioFilesCreate,
                    },
                  ],
              },
            ],
          },
          {
            name: SettingsNames.interface,
            path: 'interface',
            meta: { title: 'mainAside.interface' },
            component: InterfaceSettings,
            redirect: to => {
              const name = getEnabledSubLink(to.name)
              return { name }
            },
            children: [
              {
                name: 'aSideMenuSettings',
                path: 'aside-menu-settings',
                meta: { title: 'mainAside.personalSettings' },
                component: ASideMenuSettings,
              },
              {
                name: 'languageSettings',
                path: 'language-settings',
                meta: { title: 'mainAside.languageSettings' },
                component: LanguageSettings,
              },
              {
                name: 'chatSettings',
                path: 'chat-settings',
                meta: { title: 'mainAside.chatSettings' },
                component: ChatSettings,
                beforeEnter: chatSettingsBeforeEnter,
              },
            ],
          },
          {
            name: SettingsNames.about,
            path: 'about',
            meta: { title: 'about.aboutApp' },
            component: AboutApp,
          },
        ],
      },
      {
        name: pathNames.CONFERENCE,
        path: 'conference',
        component: Conference,
      },
    ],
  }),
  useDefaultLayout({
    path: '/login',
    name: pathNames.LOGIN,
    meta: { title: 'login.login' },
    component: ViewLogin,
  }),
  useDefaultLayout({
    path: '/bye',
    name: pathNames.BYE,
    meta: { title: 'common.byeMessage' },
    component: ByePage,
  }),
  useDefaultLayout({
    path: '/invite-login/:inviteToken',
    name: pathNames.INVITE_LOGIN,
    meta: { title: 'login.login' },
    component: ViewInviteLogin,
  }),
  useDefaultLayout({
    path: '/conference-access-denied',
    name: pathNames.CONFERENCE_INTERNAL_LINK_DENIED,
    meta: { title: 'login.internalLinkAccessDeniedTitle' },
    component: ViewConferenceInternalLinkDenied,
  }),
  useDefaultLayout({
    path: '/token-login/:accessToken',
    name: pathNames.TOKEN_LOGIN,
    meta: { title: 'login.login' },
    component: ViewTokenLogin,
  }),
  useGuestLayout({
    name: pathNames.GUEST_PREPARE_TO_CALL,
    path: '/guest-room/prepare-to-call/:roomId',
    meta: { title: 'common.session' },
    component: PrepareToCall,
    beforeEnter: beforeEnterPrepareToCall,
    props: prepareToCallPropsFn,
  }),
  useGuestLayout({
    path: '/guest-room/chat',
    name: pathNames.GUEST_ROOM_CHAT,
    meta: { title: 'common.chat' },
    component: ViewChat,
    props: guestViewChatPropsFn,
  }),
  useGuestLayout({
    path: '/guest-room/session/:sessionId',
    name: pathNames.GUEST_CALL_SESSION,
    component: GuestCallSession,
    props: callSessionPropsFn,
    meta: { title: 'common.session' },
    beforeEnter: callGuestSessionBeforeEnter,
  }),
  useSupportLayout({
    path: '/support/:guid/settings',
    name: pathNames.SUPPORT,
    redirect: ({ params }) => ({ name: pathNames.SUPPORT_SETTINGS_SUBSCRIBER_ALIASES, params }),
    children: [
      {
        name: pathNames.SUPPORT_SETTINGS_GENERAL,
        path: 'general-settings',
        component: GeneralSettings,
        redirect: redirectToLastMatched,
        children: [
          {
            name: pathNames.SUPPORT_SETTINGS_SUBSCRIBER_ALIASES,
            path: 'subscriber-aliases',
            component: SubscriberAliases,
          },
          {
            name: pathNames.SUPPORT_SETTINGS_SERVICES_HANDLE,
            path: 'services-handle',
            component: ServicesHandle,
          },
          {
            name: pathNames.SUPPORT_SETTINGS_HANDLE_SERVICES_PACKAGES,
            path: 'handle-services-packages',
            component: HandleServicesPackages,
          },
          {
            name: pathNames.SUPPORT_SETTINGS_ASIDE_MENU,
            path: 'aside-menu-settings',
            component: ASideMenuSettings,
          },
          {
            name: pathNames.SUPPORT_SETTINGS_DESKTOP_APLLICATION_SETTINGS,
            path: 'desktop-settings',
            component: DesktopApplicationSettings,
          },
          {
            name: pathNames.CONTACT_GROUPS_SETTINGS,
            path: 'contact-groups',
            component: ContactGroupsSettings,
          },
        ],
      },
      {
        name: pathNames.SUPPORT_SETTINGS_CALLS,
        path: 'calls',
        component: Calls,
        redirect: redirectToLastMatched,
        children: [
          {
            name: pathNames.SUPPORT_SETTINGS_INCOMING_CALL_HANDLING,
            path: 'incoming-call-handling',
            component: IncomingCallHandling,
          },
          {
            name: pathNames.SUPPORT_SETTINGS_BLACK_WHITE_LIST,
            path: 'black-white-list',
            component: BlackWhiteList,
            beforeEnter: blackWhiteListBeforeEnter,
          },
          {
            name: pathNames.SUPPORT_SETTINGS_FOLLOW_ME,
            path: 'follow-me',
            component: FollowMe,
            redirect: () => {
              return { name: 'support-script-list', path: 'script-list' }
            },
            children: [
              {
                name: 'support-script-list',
                path: 'script-list',
                meta: { title: 'callsSettings.followMe' },
                component: ScriptList,
              },
              {
                name: 'support-edit-script',
                path: 'edit-script/:id',
                meta: { title: 'edit-script' },
                component: EditScript,
                props: followMeEditScriptPropsFn,
              },
              {
                name: 'support-create-script',
                path: 'create-script',
                meta: { title: 'create-script' },
                component: CreateScript,
              },
            ],
          },
          {
            name: pathNames.SUPPORT_SETTINGS_SPEED_DIAL,
            path: 'speed-dial',
            component: SpeedDial,
          },
          {
            name: pathNames.SUPPORT_SETTINGS_IVR,
            path: 'ivr',
            component: IvrPage,
          },
          {
            name: pathNames.SUPPORT_SETTINGS_VOICE_MAIL_FORWARDING,
            path: 'voice-mail-forwarding',
            component: VoiceMailForwarding,
          },
          {
            name: pathNames.SUPPORT_SETTINGS_SUBSCRIBER_PICKUP,
            path: 'subscriber-pick-up',
            component: SubscriberPickUp,
          },
        ],
      },
      {
        name: pathNames.SUPPORT_SETTINGS_TERMINALS,
        path: 'terminals',
        component: TerminalsWrapper,
        redirect: redirectToLastMatched,
        children: [
          {
            name: pathNames.SUPPORT_SETTINGS_TERMINALS_LIST,
            path: 'terminals-list',
            meta: { title: 'terminals.terminalsList' },
            beforeEnter: terminalsListBeforeEnter,
            component: TerminalsList,
          },
          {
            name: pathNames.SUPPORT_SETTINGS_FUNCTIONAL_KEYS_SETUP,
            path: 'functional-keys-setup/:mac',
            meta: { title: 'terminals.autosetting' },
            component: FunctionalKeys,
            beforeEnter: functionalKeysBeforeEnter,
            props: functionalKeysPropsFn,
          },
          {
            name: pathNames.SUPPORT_SETTINGS_EQUIPMENT_LIST,
            path: 'equipment',
            meta: { title: 'common.devices' },
            beforeEnter: equipmentBeforeEnter,
            component: EquipmentList,
          },
          {
            name: pathNames.SUPPORT_SETTINGS_SESSIONS_LIST,
            path: 'sessions',
            meta: { title: 'terminals.sessions' },
            beforeEnter: sessionsBeforeEnter,
            component: SessionsList,
          },
        ],
      },
      {
        name: pathNames.SUPPORT_ACCOUNT,
        path: 'account',
        component: AccountSettings,
      },
      {
        name: pathNames.SUPPORT_CONFIDENTIALITY,
        path: 'confidentiality',
        component: ConfidentialityWrapper,
        redirect: redirectToLastMatched,
        children: [
          {
            name: pathNames.SUPPORT_PIN_PASSWORD_INPUT_SUPPORT,
            path: 'pin-password-page',
            component: PinPasswordInput,
          },
          {
            name: pathNames.SUPPORT_CLIR_SUPPORT,
            path: 'c-l-i-r',
            component: CLIR,
          },
        ],
      },
      {
        name: pathNames.SUPPORT_ACCOUNT,
        path: 'account',
        component: AccountSettings,
      },
      {
        name: pathNames.SUPPORT_SETTINGS_NOTIFICATIONS,
        path: 'notifications',
        component: NotificationsWrapper,
        redirect: redirectToLastMatched,
        children: [
          {
            name: pathNames.SUPPORT_SETTINGS_NOTIFICATIONS_SETTINGS,
            path: 'notifications-settings',
            component: NotificationSettings,
          },
          {
            name: pathNames.SUPPORT_ALARM_SCHEDULE,
            path: 'alarm-schedule',
            component: AlarmClock,
            redirect: () => {
              return { name: pathNames.SUPPORT_ALARM_SCHEDULE_MAIN }
            },
            children: [
              {
                name: pathNames.SUPPORT_ALARM_SCHEDULE_MAIN,
                path: 'alarm',
                component: AlarmPage,
                beforeEnter: alarmSettingsBeforeEnter,
              },
              {
                name: pathNames.SUPPORT_ALARM_SCHEDULE_CREATE,
                path: 'create-alarm/:id?',
                component: AlarmItem,
                props: alarmItemSettingsPropsFn,
                beforeEnter: alarmItemSettingsBeforeEnter,
              },
            ],
          },
          {
            name: pathNames.SUPPORT_AUDIO_FILES_SETTINGS,
            path: 'audio-file-settings',
            component: AudioFilesSettings,
            redirect: () => {
              return { name: 'audiofiles-list-support', path: 'audiofiles-list' }
            },
            children:
              [
                {
                  name: 'audiofiles-list-support',
                  path: 'audiofiles-list',
                  meta: { title: 'notifications.createAudioFiles' },
                  component: AudioFilesTable,
                },
                {
                  name: pathNames.SUPPORT_AUDIO_FILES_CATEGORIES,
                  path: 'audiofiles-categories',
                  meta: { title: 'notifications.createAudioFiles' },
                  component: AudioFilesCategory,
                },
                {
                  name: pathNames.SUPPORT_AUDIO_FILES_CREATE,
                  path: 'audiofiles-create',
                  meta: { title: 'notifications.newCategoryTabName' },
                  component: AudioFilesCreate,
                },
              ],
          },
        ],
      },
      {
        name: pathNames.SUPPORT_SETTINGS_INTERFACE,
        path: 'interface',
        component: InterfaceSettings,
        redirect: redirectToLastMatched,
        children: [
          {
            name: pathNames.SUPPORT_SETTINGS_THEME,
            path: 'theme-settings',
            meta: { title: 'mainAside.colorTheme' },
            component: ThemeSettings,
          },
        ],
      },
      {
        name: pathNames.SUPPORT_SETTINGS_ABOUT,
        path: 'about',
        component: AboutApp,
      },
    ],
  }),
]

const router = createRouter({
  history: createWebHashHistory(),
  routes,
})

router.beforeEach((to, from, next) => {
  clearRouteChunkError()
  return beforeEachHook(to, from, next)
})

if (typeof router.onError === 'function') {
  router.onError((error) => {
    if (!isRouteChunkLoadError(error)) {
      console.error('Router navigation error', error)
      return
    }
    console.error('Router chunk load failed', error)
    setRouteChunkError(error)
  })
}

router.afterEach(() => {
  closeAside()
})

export { clearRouteChunkError, isRouteChunkLoadError, routeChunkError, setRouteChunkError } from './route-chunk-error'
export default router
